/**
 * Helpers de tokens: resolución de tonos, semáforo de KPI y formateo de
 * métricas. Vive en el paquete de tokens (y no en cada app) para que web y
 * móvil clasifiquen y formaten exactamente igual.
 */

import type { ColorScheme } from './semantic';
import type { ToneByScheme, ToneTokens, KpiStatus } from './domain';
import {
  ticketStatus,
  priority,
  channel,
  kpiStatus,
  customerTier,
  churnRisk,
  warrantyStatus,
  campaignStatus,
  ticketType,
} from './domain';

/** Extrae el tono de un token de dominio para el tema activo. */
export function resolveTone(tone: ToneByScheme, scheme: ColorScheme): ToneTokens {
  return tone[scheme];
}

const registries = {
  ticketStatus,
  ticketType,
  priority,
  channel,
  kpiStatus,
  customerTier,
  churnRisk,
  warrantyStatus,
  campaignStatus,
} as const;

export type ToneRegistry = keyof typeof registries;

/**
 * Busca un tono por registro y clave, con degradación segura: si llega un
 * valor desconocido desde la API se devuelve un tono neutro en vez de romper.
 */
export function tokenTone(
  registry: ToneRegistry,
  key: string,
  scheme: ColorScheme,
): ToneTokens {
  const table = registries[registry] as Record<string, { tone: ToneByScheme }>;
  const entry = table[key];
  if (entry) return entry.tone[scheme];
  return kpiStatus.sinDatos.tone[scheme];
}

/** Etiqueta legible de un token de dominio, con degradación al propio valor. */
export function tokenLabel(registry: ToneRegistry, key: string): string {
  const table = registries[registry] as Record<string, { label: string }>;
  return table[key]?.label ?? key;
}

/** Nombre de icono lucide de un token de dominio. */
export function tokenIcon(registry: ToneRegistry, key: string): string {
  const table = registries[registry] as Record<string, { icon?: string }>;
  return table[key]?.icon ?? 'Circle';
}

// ── Semáforo de indicadores ─────────────────────────────────────────────────

/** Dirección deseada del indicador respecto de su meta. */
export type Goal = 'lowerIsBetter' | 'higherIsBetter';

export interface KpiEvaluation {
  status: KpiStatus;
  /** Cumplimiento 0..1+ respecto de la meta (1 = justo en la meta). */
  attainment: number;
  /** Distancia con signo a la meta, en las unidades del indicador. */
  gap: number;
}

/**
 * Clasifica un indicador contra su meta.
 *
 * `riesgo` es la banda de tolerancia previa al incumplimiento: por defecto un
 * 10 % de holgura, de modo que un TPA de 4.3 h contra una meta de 4 h aparezca
 * en ámbar y no en rojo, y el equipo pueda reaccionar antes de fallar.
 */
export function evaluateKpi(
  value: number | null | undefined,
  target: number,
  goal: Goal,
  tolerance = 0.1,
): KpiEvaluation {
  if (value == null || Number.isNaN(value)) {
    return { status: 'sinDatos', attainment: 0, gap: 0 };
  }

  if (goal === 'lowerIsBetter') {
    const gap = value - target;
    const attainment = target === 0 ? (value === 0 ? 1 : 0) : target / Math.max(value, 1e-9);
    if (value <= target) return { status: 'cumple', attainment, gap };
    if (value <= target * (1 + tolerance)) return { status: 'riesgo', attainment, gap };
    return { status: 'incumple', attainment, gap };
  }

  const gap = value - target;
  const attainment = target === 0 ? 1 : value / target;
  if (value >= target) return { status: 'cumple', attainment, gap };
  if (value >= target * (1 - tolerance)) return { status: 'riesgo', attainment, gap };
  return { status: 'incumple', attainment, gap };
}

/**
 * Metas por defecto de los indicadores de la investigación. El backend las
 * envía en `kpiTargets`; estas son el respaldo para render optimista y tests.
 */
export const defaultKpiTargets = {
  tpaHours: { target: 4, goal: 'lowerIsBetter' as Goal, label: 'Tiempo promedio de primera respuesta', unit: 'h' },
  ahtMinutes: { target: 30, goal: 'lowerIsBetter' as Goal, label: 'Tiempo promedio de atención operativa', unit: 'min' },
  fcrPct: { target: 75, goal: 'higherIsBetter' as Goal, label: 'Resolución en primer contacto', unit: '%' },
  mttrHours: { target: 24, goal: 'lowerIsBetter' as Goal, label: 'Tiempo medio de resolución', unit: 'h' },
  overduePct: { target: 5, goal: 'lowerIsBetter' as Goal, label: 'Reclamos fuera de plazo', unit: '%' },
  retentionPct: { target: 85, goal: 'higherIsBetter' as Goal, label: 'Tasa de retención', unit: '%' },
  repurchasePct: { target: 15, goal: 'higherIsBetter' as Goal, label: 'Tasa de recompra', unit: '%' },
  churnPct: { target: 10, goal: 'lowerIsBetter' as Goal, label: 'Tasa de abandono (churn)', unit: '%' },
} as const;

export type KpiKey = keyof typeof defaultKpiTargets;

// ── Formateo ────────────────────────────────────────────────────────────────

const LOCALE = 'es-PE';

/** Porcentaje con una decimal: `79.1 %`. */
export function formatPct(value: number | null | undefined, decimals = 1): string {
  if (value == null || Number.isNaN(value)) return '—';
  return `${value.toLocaleString(LOCALE, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })} %`;
}

/** Monto en soles: `S/ 12,450.00`. */
export function formatCurrency(value: number | null | undefined, currency = 'PEN'): string {
  if (value == null || Number.isNaN(value)) return '—';
  return value.toLocaleString(LOCALE, { style: 'currency', currency, minimumFractionDigits: 2 });
}

export function formatNumber(value: number | null | undefined, decimals = 0): string {
  if (value == null || Number.isNaN(value)) return '—';
  return value.toLocaleString(LOCALE, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

/**
 * Duración legible a partir de horas decimales: `3 h 12 min`, `45 min`,
 * `2 d 4 h`. Se usa en TPA, MTTR y "tiempo transcurrido" del ticket.
 */
export function formatDurationHours(hours: number | null | undefined): string {
  if (hours == null || Number.isNaN(hours)) return '—';
  const totalMinutes = Math.round(hours * 60);
  if (totalMinutes < 1) return '< 1 min';
  if (totalMinutes < 60) return `${totalMinutes} min`;

  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h < 24) return m ? `${h} h ${m} min` : `${h} h`;

  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh ? `${d} d ${rh} h` : `${d} d`;
}

export function formatDurationMinutes(minutes: number | null | undefined): string {
  if (minutes == null || Number.isNaN(minutes)) return '—';
  return formatDurationHours(minutes / 60);
}

/** Formatea el valor de un indicador según su unidad declarada. */
export function formatKpi(key: KpiKey, value: number | null | undefined): string {
  const meta = defaultKpiTargets[key];
  switch (meta.unit) {
    case '%':
      return formatPct(value);
    case 'h':
      return formatDurationHours(value);
    case 'min':
      return formatDurationMinutes(value);
    default:
      return formatNumber(value);
  }
}

/**
 * Color del delta de una métrica. Recibe si "más es mejor" para que una caída
 * del churn se pinte en verde y una caída de la retención en rojo.
 */
export function deltaTone(
  delta: number | null | undefined,
  goal: Goal,
): 'success' | 'danger' | 'neutral' {
  if (delta == null || Number.isNaN(delta) || Math.abs(delta) < 1e-9) return 'neutral';
  const improved = goal === 'higherIsBetter' ? delta > 0 : delta < 0;
  return improved ? 'success' : 'danger';
}

/** Iniciales para avatares: `Pedro Quispe` → `PQ`. */
export function initials(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Color estable para el avatar de un cliente sin foto: mismo nombre → mismo
 * color, en ambas plataformas.
 */
export function avatarTone(seed: string, palette: readonly string[]): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash |= 0;
  }
  return palette[Math.abs(hash) % palette.length];
}

// ── Puente con los enums de GraphQL ─────────────────────────────────────────

/**
 * La API expone los enums en SCREAMING_SNAKE (`EN_PROCESO`), la base los
 * guarda en snake_case (`en_proceso`) y los tokens los indexan en camelCase
 * (`enProceso`). Esta es la única traducción, y vive aquí para que web y móvil
 * no inventen cada una la suya.
 */
export function enumToTokenKey(value: string | null | undefined): string {
  if (!value) return '';
  return value
    .toLowerCase()
    .split('_')
    .map((part, i) => (i === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1)))
    .join('');
}

/** Inversa de `enumToTokenKey`: `enProceso` → `EN_PROCESO`. */
export function tokenKeyToEnum(key: string): string {
  return key.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toUpperCase();
}

/** Atajo: tono de un valor que llega directamente desde GraphQL. */
export function toneFromEnum(
  registry: ToneRegistry,
  graphqlValue: string | null | undefined,
  scheme: ColorScheme,
): ToneTokens {
  return tokenTone(registry, enumToTokenKey(graphqlValue), scheme);
}

export function labelFromEnum(registry: ToneRegistry, graphqlValue: string | null | undefined): string {
  return tokenLabel(registry, enumToTokenKey(graphqlValue));
}

export function iconFromEnum(registry: ToneRegistry, graphqlValue: string | null | undefined): string {
  return tokenIcon(registry, enumToTokenKey(graphqlValue));
}
